import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const schema = z.object({
  email: z.string().email('כתובת מייל לא תקינה'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sentToEmail, setSentToEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async ({ email }: FormValues) => {
    setStatus('sending');
    setErrorMsg(null);

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    setSentToEmail(email);
    setStatus('sent');
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-navy-dark via-navy to-navy-light flex items-center justify-center px-4"
      dir="rtl"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-business-primary flex items-center justify-center">
            <span className="text-white font-bold text-xl">F</span>
          </div>
          <div className="text-white">
            <div className="text-xl font-semibold leading-tight">FAI360 Business</div>
            <div className="text-xs text-slate-300">אשראי עסקי ליועצים</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {status === 'sent' ? (
            <SentState email={sentToEmail} onBack={() => setStatus('idle')} />
          ) : (
            <>
              <h1 className="text-2xl font-bold text-navy mb-1">כניסה למערכת</h1>
              <p className="text-sm text-slate-600 mb-6">
                הזן את כתובת המייל שלך — נשלח לך קישור כניסה חד-פעמי.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    כתובת מייל
                  </label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      dir="ltr"
                      placeholder="you@company.com"
                      className="w-full pr-10 pl-3 py-2.5 border border-slate-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-business-primary focus:border-transparent disabled:bg-slate-50"
                      disabled={status === 'sending'}
                      {...register('email')}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                {status === 'error' && errorMsg && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full flex items-center justify-center gap-2 bg-business-primary hover:bg-business-primary/90 text-white font-medium py-2.5 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      שולח קישור…
                    </>
                  ) : (
                    'שלח קישור כניסה'
                  )}
                </button>
              </form>

              <p className="mt-6 text-xs text-slate-500 text-center">
                ללא סיסמה — רק קישור חד-פעמי למייל שלך.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SentState({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <div className="text-center py-4">
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
      </div>
      <h2 className="text-xl font-bold text-navy mb-2">הקישור נשלח</h2>
      <p className="text-sm text-slate-600 mb-1">
        שלחנו קישור כניסה אל
      </p>
      <p className="text-sm font-medium text-navy mb-4" dir="ltr">
        {email}
      </p>
      <p className="text-xs text-slate-500 mb-6">
        פתח את המייל ולחץ על הקישור כדי להיכנס. הקישור תקף ל-60 דקות.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-business-primary hover:underline"
      >
        שלח למייל אחר
      </button>
    </div>
  );
}
